/**
 * `FormulationMarkdown` — renderizador minimal de markdown para el
 * contenido de los modulos del Asistente Formulador, segura por
 * construccion.
 *
 * **Por que un componente separado de `MarkdownLite`.**
 *
 * `MarkdownLite` (Sprint 18.6.2, en `@/components/MarkdownLite`) ya
 * existe para las respuestas cortas del Asistente IA del expediente
 * (`ExpedienteAssistantPanel`) y documenta explicitamente que headings y
 * separadores NO son soportados — ese asistente de chat no deberia
 * generarlos. El contenido de un modulo del Formulador es distinto: es
 * prosa estructurada de la MGA — el modelo historicamente emitio
 * `## Problema central`, `---` entre secciones, etc. — y las
 * formulaciones YA guardadas en produccion tienen esa sintaxis, aunque
 * el prompt del backend ya no la pida para contenido nuevo. Mostrar
 * `## Problema central` o `---` tal cual (texto crudo) es el bug que
 * este componente resuelve.
 *
 * Ampliar `MarkdownLite` con headings hubiera significado tocar un
 * componente compartido y ya testeado del que el chat depende para que
 * NO soporte headings — separar el renderer evita ese riesgo y mantiene
 * a cada uno enfocado en su propio contrato.
 *
 * ## Por que un renderer in-house en vez de `react-markdown`
 *
 * Mismo motivo que `MarkdownLite`: `react-markdown` + transitivas pesan
 * ~80kb gzipped para un subconjunto de 6 features. El bundle del
 * frontend acaba de bajar 53% con code splitting — no lo devolvemos por
 * un renderer de texto.
 *
 * **Seguridad por diseno**: igual que `MarkdownLite`, NUNCA se usa
 * `dangerouslySetInnerHTML`. Todo se arma con elementos React tipados —
 * si el modelo devuelve `<script>` o cualquier HTML, React lo escapa por
 * default como texto literal. No se parsean links, imagenes ni code
 * fences (mismo alcance que `MarkdownLite`).
 *
 * ## Subset soportado
 *
 * - `#`, `##`, `###` (con espacio despues) → 3 niveles de encabezado.
 *   Se renderizan como texto estilizado (no `<h1>-<h3>` reales) para no
 *   alterar el esquema de encabezados de la pagina que envuelve el
 *   modulo — este contenido vive dentro de una card, no es navegacion
 *   de documento.
 * - `---` (3 o mas guiones, linea propia) → separador `<hr>`.
 * - `**negrita**` / `__negrita__`, `*cursiva*` / `_cursiva_`.
 * - Listas con `-`/`*` (no ordenada) y `1.` (ordenada).
 * - Parrafos separados por linea en blanco; salto simple → `<br>`.
 *
 * `#### ` (4+ almohadillas) NO se trata como encabezado — el modelo no
 * deberia anidar tan profundo en un modulo de identificacion; cae a
 * parrafo normal (texto literal), igual que cualquier otra sintaxis no
 * soportada.
 *
 * ## Composicion con el resaltado del marcador financiero
 *
 * `ContentWithMarkers` (en `ModuleSection.tsx`) parte el `source` por el
 * literal `[completar con tu equipo financiero]` (guardrail ADR-021)
 * ANTES de llegar aca, y envuelve cada mitad en su propia instancia de
 * `FormulationMarkdown` — este componente no sabe nada del marcador,
 * solo formatea el texto que le toca. Ese split ya existia con
 * `MarkdownLite`; migrar `ContentWithMarkers` a este componente preserva
 * el resaltado ambar intacto.
 */

import type { ReactNode } from "react";

// --- Inline (bold/italic) ---
// Mismas regex que `MarkdownLite` (ya probadas contra falsos positivos
// como `3*5 = 15`). Se duplican aca en vez de importarlas para no acoplar
// este componente al del chat — cada uno queda libre de evolucionar su
// propio subconjunto sin coordinarse.
const BOLD_RX = /\*\*([^*]+?)\*\*|__([^_]+?)__/g;
const ITALIC_RX =
  /(?<![*A-Za-z0-9])\*([^*\s](?:[^*]*?[^*\s])?)\*(?![*A-Za-z0-9])|(?<![_A-Za-z0-9])_([^_\s](?:[^_]*?[^_\s])?)_(?![_A-Za-z0-9])/g;

const UNORDERED_ITEM_RX = /^[-*]\s+(.*)$/;
const ORDERED_ITEM_RX = /^\d+\.\s+(.*)$/;
/** `#`, `##` o `###` seguido de un espacio. 4+ almohadillas no matchean a proposito. */
const HEADING_RX = /^(#{1,3})\s+(.*)$/;
/** Linea compuesta solo por 3+ guiones (con espacios opcionales al final) → separador. */
const HR_RX = /^-{3,}\s*$/;

function renderInline(line: string): ReactNode[] {
  // Procesamos bold primero — los matches de bold consumen los `**`
  // antes de que italic pueda confundirlos con su propio marcador.
  const nodes: ReactNode[] = [];
  let cursor = 0;
  const boldMatches = Array.from(line.matchAll(BOLD_RX));
  for (const match of boldMatches) {
    const start = match.index ?? 0;
    if (start > cursor) {
      nodes.push(...renderInlineNonBold(line.slice(cursor, start)));
    }
    const inner = match[1] ?? match[2] ?? "";
    nodes.push(<strong key={`b-${start}`}>{inner}</strong>);
    cursor = start + match[0].length;
  }
  if (cursor < line.length) {
    nodes.push(...renderInlineNonBold(line.slice(cursor)));
  }
  return nodes;
}

function renderInlineNonBold(segment: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  const matches = Array.from(segment.matchAll(ITALIC_RX));
  for (const match of matches) {
    const start = match.index ?? 0;
    const inner = match[1] ?? match[2] ?? "";
    if (start > cursor) {
      nodes.push(segment.slice(cursor, start));
    }
    nodes.push(<em key={`i-${start}`}>{inner}</em>);
    cursor = start + match[0].length;
  }
  if (cursor < segment.length) {
    nodes.push(segment.slice(cursor));
  }
  return nodes;
}

// --- Bloques ---

type Block =
  | { type: "paragraph"; lines: string[] }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "heading"; level: 1 | 2 | 3; text: string }
  | { type: "hr" };

/**
 * Parsea el texto crudo en bloques. Encabezados y separadores siempre
 * cierran el bloque en curso (no se mergean entre si ni con parrafos);
 * listas consecutivas del mismo tipo se agrupan; lineas en blanco
 * separan parrafos.
 */
function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  // Normalizamos \r\n → \n para no romper en Windows.
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  let current: Block | null = null;

  const flush = () => {
    if (current) {
      blocks.push(current);
      current = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.trim() === "") {
      flush();
      continue;
    }

    if (HR_RX.test(line)) {
      flush();
      blocks.push({ type: "hr" });
      continue;
    }

    const headingMatch = line.match(HEADING_RX);
    if (headingMatch) {
      flush();
      // La regex garantiza 1-3 caracteres `#` en el grupo 1; defensivo con ?? 1.
      const level = (headingMatch[1]?.length ?? 1) as 1 | 2 | 3;
      blocks.push({ type: "heading", level, text: headingMatch[2] ?? "" });
      continue;
    }

    const ulMatch = line.match(UNORDERED_ITEM_RX);
    if (ulMatch) {
      const itemText = ulMatch[1] ?? "";
      if (current && current.type === "ul") {
        current.items.push(itemText);
      } else {
        flush();
        current = { type: "ul", items: [itemText] };
      }
      continue;
    }

    const olMatch = line.match(ORDERED_ITEM_RX);
    if (olMatch) {
      const itemText = olMatch[1] ?? "";
      if (current && current.type === "ol") {
        current.items.push(itemText);
      } else {
        flush();
        current = { type: "ol", items: [itemText] };
      }
      continue;
    }

    // Linea normal — extiende el parrafo activo o crea uno nuevo.
    if (current && current.type === "paragraph") {
      current.lines.push(line);
    } else {
      flush();
      current = { type: "paragraph", lines: [line] };
    }
  }
  flush();
  return blocks;
}

/**
 * Clases por nivel de encabezado. Texto estilizado dentro de la card del
 * modulo, no `<h1>-<h3>` reales (ver docstring del modulo arriba) — el
 * nivel 1 es el mas grande, pero ninguno compite visualmente con los
 * titulos reales de la pagina (`h1`/`h2` del documento de formulacion).
 */
const HEADING_CLASS: Record<1 | 2 | 3, string> = {
  1: "text-base font-bold text-slate-900",
  2: "text-sm font-bold text-slate-900",
  3: "text-sm font-semibold text-slate-800",
};

export function FormulationMarkdown({ source }: { source: string }) {
  const blocks = parseBlocks(source);
  return (
    <div data-testid="formulation-markdown" className="space-y-2">
      {blocks.map((block, idx) => {
        if (block.type === "hr") {
          return <hr key={idx} className="my-3 border-t border-slate-200" />;
        }
        if (block.type === "heading") {
          return (
            <p key={idx} className={HEADING_CLASS[block.level]}>
              {renderInline(block.text)}
            </p>
          );
        }
        if (block.type === "ul") {
          return (
            <ul key={idx} className="ml-5 list-disc space-y-1">
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={idx} className="ml-5 list-decimal space-y-1">
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ol>
          );
        }
        // paragraph — lineas internas con <br/>
        return (
          <p key={idx} className="whitespace-pre-wrap">
            {block.lines.map((line, j) => (
              <span key={j}>
                {renderInline(line)}
                {j < block.lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
